/**
 * Validates a choice object
 * @example
 * const choice = {
 *   componentGroupId: 'componentGroupId',
 *   variantId: 'variantId',
 *   slotId: 'slotId',
 *   index: 0,
 *   slotType: 'COMPONENT'
 *   lineItemPropertyKey: 'lineItemPropertyKey',
 *   lineItemPropertyKeyValue: 'lineItemPropertyKeyValue'
 *   stepId: 'stepId'
 * };
 * @param {ChoicePayload} choice - The choice object to validate
 * @param {number} index - The index of the choice in the array
 * @returns {string[]|[]} Error message if validation fails, null if valid
 */

export const validateChoice = (
  { slotType, slotId, componentGroupId, variantId, lineItemPropertyKey, lineItemPropertyKeyValue, stepId },
  index
) => {
  const errors: string[] = [];
  const stringifiedChoice = JSON.stringify({
    slotType,
    slotId,
    componentGroupId,
    variantId,
    lineItemPropertyKey,
    lineItemPropertyKeyValue,
    stepId
  });

  if (!slotType) {
    errors.push(`Invalid choice at index ${index}: missing required properties (slotType) data: ${stringifiedChoice}`);
  }

  if (!slotId) {
    errors.push(`Invalid choice at index ${index}: missing required properties (slotId) data: ${stringifiedChoice}`);
  }

  if (['COMPONENT', 'SEQUENTIAL'].includes(slotType)) {
    if (!componentGroupId) {
      errors.push(
        `Invalid choice at index ${index}: missing required properties (componentGroupId) data: ${stringifiedChoice}`
      );
    }

    if (!variantId) {
      errors.push(
        `Invalid choice at index ${index}: missing required properties (variantId) data: ${stringifiedChoice}`
      );
    }
  }

  if (slotType === 'ALPHANUMERIC') {
    if (!lineItemPropertyKey) {
      errors.push(
        `Invalid choice at index ${index}: missing required properties (lineItemPropertyKey) data: ${stringifiedChoice}`
      );
    }

    if (!lineItemPropertyKeyValue) {
      errors.push(
        `Invalid choice at index ${index}: missing required properties (lineItemPropertyKeyValue) data: ${stringifiedChoice}`
      );
    }
  }

  return errors;
};

/**
 * Validates an addon object
 * @example
 * const addon = {
 *   addonItemId: 'addonItemId',
 *   variantId: 'variantId',
 *   stepId: 'stepId',
 *   index: 0
 * };
 * @param {Addon} addon - The addon object to validate
 * @param {number} index - The index of the addon in the array
 * @returns {string|null} Error message if validation fails, null if valid
 */
export const validateAddon = (addon, index) => {
  if (!addon.addonItemId || !addon.variantId || !addon.stepId) {
    return `Invalid addon at index ${index}: missing required properties (addonItemId, variantId, stepId) data: ${JSON.stringify(addon)}`;
  }
  return null;
};

/**
 * Validates a cart item object
 * @example
 * const cartItem = {
 *   id: 'variantId',
 *   quantity: 1,
 *   properties: { _personalisation_choices: '[]' }
 * };
 * @param {CartItem} cartItem - The cart item object to validate
 * @param {number} index - The index of the cart item in the array
 * @param {import('./personalisationData').Slot[]} slots - The slots to validate
 * @returns {string|null} Error message if validation fails, null if valid
 */
export const validateCartItem = ({ id, quantity, properties }, index, slots) => {
  if (!id || typeof quantity !== 'number' || quantity <= 0) {
    return `Invalid cart item at index ${index}: missing required properties (id, quantity) or invalid quantity data: ${JSON.stringify({ id, quantity, properties })}`;
  }

  if (!properties) {
    return `Invalid cart item at index ${index}: missing required properties (properties) data: ${JSON.stringify({ id, quantity, properties })}`;
  }

  if (!Array.isArray(properties._personalisation_choices)) {
    return `Invalid cart item at index ${index}: _personalisation_choices must be an array. data: ${JSON.stringify({ id, quantity, properties })}`;
  }

  const malformedPersonalisationChoices = properties._personalisation_choices.filter(
    (choice) => !choice.c || !choice.st || !choice.sl || typeof choice.i !== 'number'
  );

  if (malformedPersonalisationChoices.length > 0) {
    const malformedPersonalisationChoicesString = malformedPersonalisationChoices
      .map((choice) => `{c: ${choice.c}, st: ${choice.st}, sl: ${choice.sl}, i: ${choice.i}}`)
      .join(', ');
    return `Invalid cart item at index ${index}: malformed personalisation choices: ${malformedPersonalisationChoicesString}`;
  }

  return null;
};
